import { ErrorMessage, Field, useFormikContext } from "formik";
import { AsteriskIcon } from "lucide-react";
import React from "react";

interface FormikSelectProps {
  label?: string;
  name: string;
  selectedOption: string;
  optionData?: Record<string, any>[];
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  value?: string | number;
  disable?: boolean;
  divClasses?: string;
  valueProperty?: string;
  labelProperty?: string;
  label2propery?: string;
  brackets?: boolean;
  className?: string;
  enableRedAsterick?: boolean;
  disableOptionProperty?: string;
  disabledOptionText?: string;
}
export const FormikSelect = ({
  label,
  name,
  selectedOption,
  optionData,
  value,
  disable,
  divClasses,
  valueProperty = "name",
  labelProperty = "name",
  label2propery,
  brackets,
  className,
  enableRedAsterick = false,
  disableOptionProperty = "disable",
  disabledOptionText = "",
  onChange,
}: FormikSelectProps) => {
  return (
    <div className={`w-full ${divClasses}`}>
      {label && (
        <label
          className={`mb-1.5 text-sm font-medium text-slate-700 capitalize flex`}
        >
          {label}
          {enableRedAsterick && (
            <AsteriskIcon className="size-3 text-red-500" />
          )}
        </label>
      )}
      <Field
        name={name}
        as="select"
        value={value}
        disabled={disable}
        onChange={onChange}
        className={`${className} rounded-[2.5rem] block w-full px-3 py-2.5 text-slate-700 placeholder-slate-400 placeholder:capitalize bg-white border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all`}
      >
        <option selected value="" disabled className="capitalize">
          {selectedOption}
        </option>
        {optionData?.map((option, index) => (
          <option
            key={index}
            value={option[valueProperty]}
            disabled={option[disableOptionProperty]}
          >
            {option[labelProperty]}
            {label2propery
              ? brackets
                ? ` (${option[label2propery]})`
                : option[label2propery]
              : ""}{" "}
            {option[disableOptionProperty] && `(${disabledOptionText})`}
          </option>
        ))}
      </Field>
      <ErrorMessage
        name={name}
        className="text-red-500 text-sm mt-1"
        component="div"
      />
    </div>
  );
};

interface FormikInputProps {
  label: string;
  name: string;
  type: string;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  readOnly?: boolean;
  divClasses?: string;
  length?: number;
  disable?: boolean;
  min?: number;
  max?: number;
  convertToText?: boolean;
  enableRedAsterick?: boolean;
  case_sensitivity?: "normal" | "lowercase" | "uppercase";
  [key: string]: any;
}
export const FormikInput = ({
  label,
  name,
  type,
  placeholder,
  className,
  labelClassName,
  readOnly,
  divClasses,
  length,
  disable,
  min = 0,
  max,
  convertToText = false,
  enableRedAsterick = false,
  case_sensitivity = "normal",
  ...props
}: FormikInputProps) => {
  const { setFieldValue } = useFormikContext();

  const handleCaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (type === "number" && min !== undefined) {
      if (value !== "" && Number(value) < min) return;
    }

    switch (case_sensitivity) {
      case "lowercase":
        value = value.toLowerCase();
        break;
      case "uppercase":
        value = value.toUpperCase();
        break;
      case "normal":
      default:
        break;
    }

    setFieldValue(name, value);
  };

  return (
    <div className={`w-full ${divClasses}`}>
      <label
        className={`${labelClassName} mb-1.5 text-sm font-medium text-slate-700 capitalize flex`}
      >
        {label}
        {enableRedAsterick && <AsteriskIcon className="size-3 text-red-500" />}
      </label>
      <Field
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        type={type}
        maxLength={length}
        disabled={disable}
        max={max}
        min={min}
        onChange={handleCaseChange}
        {...props}
        className={`${className} rounded-[2.5rem] block w-full px-3 py-2.5 text-slate-700 placeholder-slate-400 placeholder:capitalize bg-white border border-slate-300/80 focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all disabled:opacity-70`}
      />

      <ErrorMessage
        name={name}
        className="text-red-500 text-sm mt-1"
        component="div"
      />
    </div>
  );
};

interface FormikTextareaProps {
  label: string;
  name: string;
  placeholder?: string;
  readOnly?: boolean;
  divClasses?: string;
  className?: string;
  length?: number;
  disable?: boolean;
  min?: number;
  max?: number;
  enableRedAsterick?: boolean;
}
export const FormikTextarea = ({
  label,
  name,
  placeholder,
  readOnly,
  divClasses = "",
  className = "",
  length,
  disable,
  min,
  max,
  enableRedAsterick = false,
}: FormikTextareaProps) => {
  return (
    <div className={`w-full ${divClasses}`}>
      <label
        className={`mb-1.5 text-sm font-medium text-slate-700 capitalize flex`}
      >
        {label}
        {enableRedAsterick && <AsteriskIcon className="size-3 text-red-500" />}
      </label>
      <Field
        as="textarea"
        name={name}
        placeholder={placeholder}
        readOnly={readOnly}
        maxLength={length}
        disabled={disable}
        max={max}
        min={min}
        className={`${className} block w-full px-4 py-3 text-slate-700 placeholder-slate-400 placeholder:capitalize bg-white border border-slate-300/80 rounded-2xl focus:border-blumine-500 focus:ring-2 focus:ring-blumine-500/50 focus:outline-none transition-all disabled:opacity-70`}
      />
      <ErrorMessage
        name={name}
        className="text-red-500 text-sm mt-1"
        component="div"
      />
    </div>
  );
};

interface SwitchProps {
  isOn: boolean;
  handleToggle: () => void;
  activeColor?: string;
  inactiveColor?: string;
  size?: "small" | "medium" | "large";
  onLabel?: string;
  offLabel?: string;
  label?: string;
  divClasses?: string;
  labelClasses?: string;
  disable?: boolean;
}

export const Switch = ({
  isOn,
  handleToggle,
  activeColor = "bg-green-500",
  inactiveColor = "bg-gray-300",
  size = "medium",
  onLabel,
  offLabel,
  label,
  divClasses,
  labelClasses,
  disable = false,
}: SwitchProps) => {
  const sizeClasses = {
    small: { width: 28, height: 18, handle: 12, labelSize: 12, gap: 4 },
    medium: { width: 48, height: 28, handle: 20, labelSize: 16, gap: 6 },
    large: { width: 64, height: 36, handle: 28, labelSize: 20, gap: 10 },
  };

  const selectedSize = sizeClasses[size] || sizeClasses.medium;

  return (
    <div className={`w-full ${divClasses}`}>
      {label && (
        <label
          className={`block mb-2 text-sm text-gray-600 ${disable && "text-gray-400!"} ${labelClasses}`}
        >
          {label}
        </label>
      )}
      <div style={{ gap: selectedSize.gap }} className="flex items-center">
        <div
          className={`flex items-center rounded-full cursor-pointer relative ${isOn ? activeColor : inactiveColor} ${
            disable && "opacity-50 cursor-not-allowed"
          }`}
          onClick={disable ? () => {} : handleToggle}
          role="button"
          aria-pressed={isOn}
          style={{
            width: selectedSize.width,
            height: selectedSize.height,
          }}
        >
          <div
            className="bg-white rounded-full shadow-sm ease-in duration-200"
            style={{
              width: selectedSize.handle,
              height: selectedSize.handle,
              transform: `translateX(${isOn ? selectedSize.width - selectedSize.handle - 3 : 3}px)`,
              transition: "transform 0.2s ease-in-out",
            }}
          />
        </div>
        {onLabel && offLabel && (
          <span style={{ fontSize: selectedSize.labelSize }}>
            {isOn ? onLabel : offLabel}
          </span>
        )}
      </div>
    </div>
  );
};

interface FormikMultiSelectProps {
  label: string;
  name: string;
  options: Record<string, any>[];
  placeholder?: string;
  valueProperty?: string;
  labelProperty?: string;
  divClasses?: string;
  enableRedAsterick?: boolean;
  disabled?: boolean;
  returnObjects?: boolean;
}

export const FormikMultiSelect = ({
  label,
  name,
  options,
  placeholder = "Select options...",
  valueProperty = "name",
  labelProperty = "name",
  divClasses = "",
  enableRedAsterick = false,
  disabled = false,
  returnObjects = false,
}: FormikMultiSelectProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedValues = Array.isArray(values[name])
    ? values[name]
    : values[name]
      ? [values[name]]
      : [];

  const isSelected = (optionValue: any) => {
    return selectedValues.some((v: any) =>
      typeof v === "object" && v !== null
        ? v[valueProperty] === optionValue
        : v === optionValue,
    );
  };

  const toggleOption = (optionValue: any) => {
    let newValues;
    if (isSelected(optionValue)) {
      newValues = selectedValues.filter((v: any) =>
        typeof v === "object" && v !== null
          ? v[valueProperty] !== optionValue
          : v !== optionValue,
      );
    } else {
      const newValue = returnObjects
        ? options.find((opt) => opt[valueProperty] === optionValue)
        : optionValue;
      newValues = [...selectedValues, newValue];
    }
    setFieldValue(name, newValues);
  };

  const removeOption = (e: React.MouseEvent, optionValue: any) => {
    e.stopPropagation();
    const newValues = selectedValues.filter((v: any) =>
      typeof v === "object" && v !== null
        ? v[valueProperty] !== optionValue
        : v !== optionValue,
    );
    setFieldValue(name, newValues);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`w-full ${divClasses}`}>
      {label && (
        <label className="mb-1.5 text-sm font-medium text-slate-700 capitalize flex items-center gap-1">
          {label}
          {enableRedAsterick && (
            <AsteriskIcon className="size-3 text-red-500" />
          )}
        </label>
      )}

      <div className="w-full relative" ref={containerRef}>

      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-[46px] w-full px-3 py-1.5 bg-white border rounded-3xl transition-all cursor-pointer flex flex-wrap gap-2 items-center ${
          isOpen
            ? "border-blumine-500 ring-2 ring-blumine-500/20"
            : "border-slate-300/80"
        } ${disabled ? "bg-slate-50 cursor-not-allowed opacity-70" : "hover:border-slate-400"}`}
      >
        {selectedValues.length === 0 ? (
          <span className="text-slate-400 text-sm ml-1">{placeholder}</span>
        ) : (
          selectedValues.map((val: any) => {
            const actualValue =
              typeof val === "object" && val !== null
                ? val[valueProperty]
                : val;
            const option = options.find(
              (opt) => opt[valueProperty] === actualValue,
            );
            return (
              <div
                key={actualValue}
                className="flex items-center gap-1.5 bg-slate-100 text-slate-700 px-2.5 py-1 rounded-lg border border-slate-200 text-xs font-semibold animate-in fade-in zoom-in duration-200"
              >
                {option ? option[labelProperty] : actualValue}
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => removeOption(e, actualValue)}
                    className="hover:text-red-500 transition-colors"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200 custom-scrollbar">
          <div className="p-1">
            {options.filter((opt) => !isSelected(opt[valueProperty])).length >
            0 ? (
              options
                .filter((opt) => !isSelected(opt[valueProperty]))
                .map((option, index) => {
                  return (
                    <div
                      key={index}
                      onClick={() => toggleOption(option[valueProperty])}
                      className="px-4 py-2.5 text-sm rounded-xl cursor-pointer transition-colors flex items-center justify-between text-slate-600 hover:bg-slate-50"
                    >
                      {option[labelProperty]}
                    </div>
                  );
                })
            ) : (
              <div className="px-4 py-3 text-xs text-slate-400 text-center font-medium">
                No more options available
              </div>
            )}
          </div>
        </div>
      )}

      </div>

      <ErrorMessage
        name={name}
        className="text-red-500 text-sm mt-1"
        component="div"
      />
    </div>
  );
};

export interface MultiSelectProps {
  label?: string;
  options: Record<string, any>[];
  value: any[];
  onChange: (value: any[]) => void;
  placeholder?: string;
  valueProperty?: string;
  labelProperty?: string;
  divClasses?: string;
  returnObjects?: boolean;
}

export const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select options...",
  valueProperty = "name",
  labelProperty = "name",
  divClasses = "",
  returnObjects = false,
}: MultiSelectProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isSelected = (optionValue: any) => {
    return value.some((v: any) =>
      typeof v === "object" && v !== null
        ? v[valueProperty] === optionValue
        : v === optionValue,
    );
  };

  const toggleOption = (optionValue: any) => {
    let newValues;
    if (isSelected(optionValue)) {
      newValues = value.filter((v: any) =>
        typeof v === "object" && v !== null
          ? v[valueProperty] !== optionValue
          : v !== optionValue,
      );
    } else {
      const newValue = returnObjects
        ? options.find((opt) => opt[valueProperty] === optionValue)
        : optionValue;
      newValues = [...value, newValue];
    }
    onChange(newValues);
  };

  const removeOption = (e: React.MouseEvent, optionValue: any) => {
    e.stopPropagation();
    const newValues = value.filter((v: any) =>
      typeof v === "object" && v !== null
        ? v[valueProperty] !== optionValue
        : v !== optionValue,
    );
    onChange(newValues);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`w-full ${divClasses}`}>
      {label && (
        <label className="mb-1.5 text-xs font-bold text-slate-600 ml-1 uppercase tracking-wider">
          {label}
        </label>
      )}

      <div className="w-full relative" ref={containerRef}>

      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`min-h-[40px] w-full px-3 py-1.5 bg-slate-50/50 border border-slate-200/80 rounded-xl transition-all cursor-pointer flex flex-wrap gap-1.5 items-center ${
          isOpen
            ? "border-blumine-500 bg-white shadow-sm ring-2 ring-blumine-500/10"
            : "hover:border-slate-300"
        }`}
      >
        {value.length === 0 ? (
          <span className="text-slate-400 text-[13px] ml-1">{placeholder}</span>
        ) : (
          value.map((val: any) => {
            const actualValue =
              typeof val === "object" && val !== null
                ? val[valueProperty]
                : val;
            const option = options.find(
              (opt) => opt[valueProperty] === actualValue,
            );
            return (
              <div
                key={actualValue}
                className="flex items-center gap-1 bg-white text-slate-700 px-2 py-0.5 rounded-lg border border-slate-200 text-[11px] font-bold shadow-sm animate-in zoom-in-95 duration-150"
              >
                {option ? option[labelProperty] : actualValue}
                <button
                  type="button"
                  onClick={(e) => removeOption(e, actualValue)}
                  className="hover:text-red-500 transition-colors ml-0.5"
                >
                  ×
                </button>
              </div>
            );
          })
        )}
      </div>

      {isOpen && (
        <div className="absolute z-[110] w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200 custom-scrollbar">
          <div className="p-1">
            {options.filter((opt) => !isSelected(opt[valueProperty])).length >
            0 ? (
              options
                .filter((opt) => !isSelected(opt[valueProperty]))
                .map((option, index) => {
                  return (
                    <div
                      key={index}
                      onClick={() => toggleOption(option[valueProperty])}
                      className="px-3 py-2 text-[12px] rounded-lg cursor-pointer transition-colors flex items-center justify-between text-slate-600 hover:bg-slate-50"
                    >
                      {option[labelProperty]}
                    </div>
                  );
                })
            ) : (
              <div className="px-3 py-3 text-[10px] text-slate-400 text-center font-bold uppercase tracking-wider">
                All selected
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
  );
};
